import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild} from "@angular/core";
import {formatMeasurementFileSize, FoundMeasurement} from "../found-measurement";
import {UploadFormConfig} from "../../upload/upload-form-config";
import {emptyUploadFormModel} from "../../upload/upload-form-model";
import {localizationKey, LocalizationStrings} from "../../../i18n/i18n-model";
import {isEcgMeasurement} from "../../measurement-models";
import {emptyDataOfficerMeasurement} from "../list-spec-fixtures.test-util";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {MeasurementDetailsService} from "./measurement-details.service";
import {EditHolterTypeService} from "../edit-holter-type/edit-holter-type.service";
import {FormFieldConfig} from "../../../tools/form/form-field-config";
import {RepeatMeasurementService} from "../repeat-measurement/repeat-measurement.service";
import {ReleaseToggleState} from "../../../tools/release-toggles/release-toggle-state";
import {RevertFromDoneService} from "../revert-from-done/revert-from-done.service";
import {ActionButtonState} from "../../../tools/confirm-action-button/pre-confirm-action-button.component";
import {finalize} from "rxjs/operators";
import {Notifications, NotificationType} from "../../../tools/notifications/notifications";
import {RejectStudyService} from "../reject/reject-study.service";
import {ReturnToPreanalysisService} from "../return-to-preanalysis/return-to-preanalysis.service";

interface MeasurementMetadata {
  readonly fileName: string;
  readonly dataSize: string;
  readonly specifiedHomeHolterDuration: string | undefined;
}

@Component({
    selector: "app-measurement-details",
    templateUrl: "./measurement-details.component.html",
    standalone: false
})
export class MeasurementDetailsComponent implements OnChanges {
  @Input() measurement: FoundMeasurement = emptyDataOfficerMeasurement();

  @Output() measurementChanged: EventEmitter<FoundMeasurement> = new EventEmitter<FoundMeasurement>();

  readonly labelStart: keyof LocalizationStrings = "analysisLabelStartTime";

  patientDataFields?: UploadFormConfig;

  modalRef: BsModalRef | undefined;
  @ViewChild("templateEditMeasurement") templateEditMeasurement!: TemplateRef<any>;
  @ViewChild("templateEditHolterType") templateEditHolterType!: TemplateRef<any>;

  fileNameConfig: FormFieldConfig<MeasurementMetadata> | undefined;
  dataSizeConfig: FormFieldConfig<MeasurementMetadata> | undefined;
  specifiedHomeHolterDurationConfig: FormFieldConfig<MeasurementMetadata> | undefined;

  constructor(
    private readonly modalService: BsModalService,
    private readonly measurementDetailsService: MeasurementDetailsService,
    private readonly editHolterTypeService: EditHolterTypeService,
    private readonly repeatMeasurementService: RepeatMeasurementService,
    private readonly revertFromDoneService: RevertFromDoneService,
    private readonly notifications: Notifications,
    private readonly rejectStudyService: RejectStudyService,
    private readonly returnToPreanalysisService: ReturnToPreanalysisService
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.measurement) {
      return;
    }

    this.patientDataFields = new UploadFormConfig({
      model: {
        ...emptyUploadFormModel(),
        ...this.measurement.patientData,
      },
      usesDateOfBirth: this.measurement.usesDateOfBirth,
    });

    const metadata: MeasurementMetadata = {
      fileName: this.measurement.fileName || "",
      dataSize: formatMeasurementFileSize(this.measurement.dataSize),
      specifiedHomeHolterDuration: this.measurement.specifiedHomeHolterDuration + " d"
    };

    this.fileNameConfig = {
      label: "measurementDetailsFileNameLabel",
      property: "fileName",
      type: "text",
      isValid: () => true,
      model: metadata
    };

    this.dataSizeConfig = {
      label: "measurementDetailsDataSizeLabel",
      property: "dataSize",
      type: "text",
      isValid: () => true,
      model: metadata
    };

    if (this.measurement.specifiedHomeHolterDuration !== undefined) {
      this.specifiedHomeHolterDurationConfig =
        {
          label: "measurementDetailsSpecifiedHomeHolterDurationLabel",
          property: "specifiedHomeHolterDuration",
          type: "text",
          isValid: () => true,
          model: metadata
        };
    }


  }

  isEcgListItem(listItem: FoundMeasurement): boolean {
    return isEcgMeasurement(listItem.type);
  }

  showEditButton() {
    return this.measurementDetailsService.canEditMeasurement(this.measurement.status);
  }

  protected readonly localizationKey = localizationKey;

  showEditHolterTypeButton(): boolean {
    return this.measurementDetailsService.canEditHolterType(this.measurement.type, this.measurement.status);
  }

  showEditMeasurementModal() {
    this.modalRef = this.modalService.show(this.templateEditMeasurement, {
      class: "modal-lg"
    });
  }

  onEditMeasurementSuccess(measurement: FoundMeasurement) {
    this.modalRef?.hide();
    this.measurementChanged.emit(measurement);
  }

  closeEditor() {
    this.modalRef?.hide();
  }

  openHolterEditor() {
    this.modalRef = this.modalService.show(this.templateEditHolterType, {
      class: "modal-sm"
    });
  }

  onHolterTypeSet(holterDurationDays: number) {
    this.closeEditor();
    this.editHolterTypeService.setHolterType(
      this.measurement.id,
      holterDurationDays
    )
      .subscribe((newMeasurement) => {
        this.measurementChanged.emit(newMeasurement);
      });
  }

  protected readonly formatMeasurementFileSize = formatMeasurementFileSize;

  readonly feature18Released = ReleaseToggleState.getInstance().isReleased("REVERT_FROM_DONE_18");
  readonly feature516Released = ReleaseToggleState.getInstance().isReleased("REJECT_MEASUREMENT_IN_MEASUREMENT_DETAILS_516");
  readonly feature616Released = ReleaseToggleState.getInstance().isReleased("REPEAT_TECHNICIAN_ANALYSIS_616");

  revertFromDoneState: ActionButtonState = "INITIAL";
  repeatMeasurementState: ActionButtonState = "INITIAL";
  rejectMeasurementState: ActionButtonState = "INITIAL";
  returnToPreanalysisState: ActionButtonState = "INITIAL";

  canRepeatMeasurement() {
    return this
      .measurementDetailsService
      .canRepeatMeasurement(
        this.measurement.status,
        this.measurement.homeStatus
      );
  }

  repeatMeasurement() {

    if (this.repeatMeasurementState === "INITIAL") {
      this.repeatMeasurementState = "CONFIRMATION";
      return;
    }

    this.repeatMeasurementState = "ACTION_IN_PROGRESS";

    this.repeatMeasurementService.repeat(this.measurement.id)
      .pipe(finalize(() => this.repeatMeasurementState = "INITIAL"))
      .subscribe({
        next: (newMeasurement) => this.measurementChanged.emit(newMeasurement),
        error: () => {
        }
      });
  }

  canRevertFromDone() {
    return this
      .measurementDetailsService
      .canRevertFromDone(
        this.measurement.status,
        this.measurement.nurseDownloadedReport
      );
  }

  revertFromDone() {
    if (this.revertFromDoneState === "INITIAL") {
      this.revertFromDoneState = "CONFIRMATION";
      return;
    }

    this.revertFromDoneState = "ACTION_IN_PROGRESS";

    this
      .revertFromDoneService
      .revertFromDone(this.measurement.id)
      .pipe(finalize(() => this.revertFromDoneState = "INITIAL"))
      .subscribe({
        next: (changedMeasurement) => this.measurementChanged.emit(changedMeasurement),
        error: () => this.notifications.addNotification(NotificationType.ERROR, "revertMeasurementFromDoneFailureMessage")
      });
  }

  rejectMeasurement() {

    if (this.rejectMeasurementState === "INITIAL") {
      this.rejectMeasurementState = "CONFIRMATION";
      return;
    }

    this.rejectMeasurementState = "ACTION_IN_PROGRESS";

    this.rejectStudyService.reject(this.measurement.id)
      .pipe(
        finalize(() => this.rejectMeasurementState = "INITIAL")
      )
      .subscribe({
        next: (changedMeasurement) => this.measurementChanged.emit(changedMeasurement),
        error: () => this.notifications.addNotification(NotificationType.ERROR, "rejectStudyFailed")
      });
  }

  canRejectMeasurement(): boolean {

    if (this.measurement.status === "INITIATED"
      && ReleaseToggleState.getInstance().isReleased("REJECT_INITIATED_HOME_MEASUREMENT_515")
    ) {
      return this.measurement.homeStatus !== undefined
        && this.measurement.homeStatus !== "WAITING_FOR_MAILING_INFO";
    }

    return ![
      "REJECTED_BY_DATA_OFFICER",
      "INITIATED",
      "DONE"
    ]
      .includes(this.measurement.status);
  }

  canReturnToPreanalysis(): boolean {
    return this.measurementDetailsService.canReturnToPreanalysis(
      this.measurement.type,
      this.measurement.status,
      this.measurement.externalAnalysisType
    );
  }

  returnToPreanalysis() {
    if (this.returnToPreanalysisState === "INITIAL") {
      this.returnToPreanalysisState = "CONFIRMATION";
      return;
    }

    this.returnToPreanalysisState = "ACTION_IN_PROGRESS";

    this.returnToPreanalysisService
      .returnToPreanalysis(this.measurement.id)
      .pipe(finalize(() => this.returnToPreanalysisState = "INITIAL"))
      .subscribe({
        next: measurement => {
          this.notifications.addNotification(NotificationType.OK, "returnToPreanalysisSucceeded");
          this.measurementChanged.emit(measurement);
        },
        error: () => this.notifications.addNotification(NotificationType.ERROR, "returnToPreanalysisFailed")
      });
  }
}
